import type { LocationName } from '@game/game-data';
import type { ClientCombatantStatic, ClientCombatantStatsDelta, ClientCombatRound, ClientCombatTurnUpdate } from '@game/shared-types';
import { create } from 'zustand';

import { mergeByProperty } from '@util/mergeByProperty';

import type { CombatRoundCombatantsStats, CombatTurnData } from '../types/combat.types';
import type { CombatState } from './combatState';
import { combatDefault } from './combatState';

export enum CombatScreenState {
  InCombat,
  CombatTimedOut,
  NotInCombat,
  CombatWon,
  CombatLost,
  CombatTie,
}

interface Store extends CombatState {
  resetCombat: () => void;
  setCombatLogHistory: (combatLog: CombatTurnData[]) => void;
  addCombatLogHistory: (combatLog: CombatTurnData) => void;
  setCombatRoundCombatantsStats: (combatRoundCombatantsStats: CombatRoundCombatantsStats[]) => void;
  setCombatCurrentRound: (combatRound: ClientCombatRound) => void;
  appendTurnToCurrentRound: (turnUpdate: ClientCombatTurnUpdate) => void;
  setCombatants: (combatants: ClientCombatantStatic[]) => void;
  addTurnToCombatTurnQueue: (combatTurn: CombatTurnData) => void;
  filterSequenceKeyFromCombatTurnQueue: (sequenceKey: string) => void;
  setTurnExpireAt: (turnExpireAt: number) => void;
  setCombatLocationName: (combatLocationName: LocationName) => void;
  combatScreenState: CombatScreenState;
}

export const combatStore = create<Store>((set) => ({
  ...combatDefault,
  combatScreenState: CombatScreenState.NotInCombat,
  resetCombat: (): void => {
    set((state) => ({ ...state, ...combatDefault }));
  },
  setCombatLogHistory: (combatLog: CombatTurnData[]): void => {
    const filteredCombatLog = combatLog.filter((cl) => cl.turn.actions[0] && cl.turn.actions[0].skillName !== 'Skip');

    if (filteredCombatLog.length > 0) {
      set((state) => ({
        ...state,
        combatLogHistory: [...filteredCombatLog, ...state.combatLogHistory],
      }));
    }
  },
  addCombatLogHistory: (combatLog: CombatTurnData): void => {
    if (combatLog.turn.actions[0] && combatLog.turn.actions[0].skillName === 'Skip') {
      return;
    }

    set((state) => ({
      ...state,
      combatLogHistory: [combatLog, ...state.combatLogHistory],
    }));
  },
  setCombatRoundCombatantsStats: (combatRoundCombatantsStats: CombatRoundCombatantsStats[]): void => {
    set((state) => ({
      ...state,
      combatRoundCombatantsStats: mergeByProperty(state.combatRoundCombatantsStats, combatRoundCombatantsStats, 'roundNumber'),
    }));
  },
  setCombatCurrentRound: (combatRound: ClientCombatRound): void => {
    set((state) => {
      return state.previousRound.roundNumber === combatRound.roundNumber
        ? {
            ...state,
            currentRound: combatRound,
          }
        : {
            ...state,
            previousRound: { ...state.currentRound },
            currentRound: combatRound,
          };
    });
  },
  setCombatants: (combatants: ClientCombatantStatic[]): void => {
    set((state) => ({
      ...state,
      combatants,
    }));
  },
  appendTurnToCurrentRound: (turnUpdate: ClientCombatTurnUpdate): void => {
    set((state) => {
      const updatedStats = state.currentRound.combatantsStats.map((existing) => {
        const delta = turnUpdate.combatantsStats.find((d: ClientCombatantStatsDelta) => d.id === existing.id && d.type === existing.type);
        if (!delta) {
          return existing;
        }
        return {
          ...existing,
          healthCurrent: delta.healthCurrent,
          fled: delta.fled,
          actionEffects: delta.actionEffects ?? [],
          actionCooldowns: delta.actionCooldowns ?? [],
          passiveEffects: delta.passiveEffects ?? [],
          passiveCooldowns: delta.passiveCooldowns ?? [],
          actionCharges: delta.actionCharges ?? [],
        };
      });

      const updatedRound = {
        ...state.currentRound,
        turns: [...state.currentRound.turns, turnUpdate.turn],
        combatantsStats: updatedStats,
        turnExpireAt: turnUpdate.turnExpireAt,
      };

      return state.previousRound.roundNumber === updatedRound.roundNumber
        ? { ...state, currentRound: updatedRound }
        : { ...state, previousRound: { ...state.currentRound }, currentRound: updatedRound };
    });
  },
  addTurnToCombatTurnQueue: (combatTurn: CombatTurnData): void => {
    set((state) => ({
      ...state,
      turnQueue: [combatTurn, ...state.turnQueue],
    }));
  },
  filterSequenceKeyFromCombatTurnQueue: (sequenceKey: string): void => {
    set((state) => ({
      ...state,
      turnQueue: state.turnQueue.filter((t) => t.turn.sequenceKey !== sequenceKey),
    }));
  },
  setTurnExpireAt: (turnExpireAt: number): void => {
    set((state) => ({
      ...state,
      currentRound: {
        ...state.currentRound,
        turnExpireAt,
      },
    }));
  },
  setCombatLocationName: (combatLocationName: string): void => {
    set((state) => ({
      ...state,
      combatLocationName,
    }));
  },
}));
